/**
 * 「연금술사가 되었다」 서버 호출 — 같은 출처 상대 경로만 쓴다(routes/pop-web.php).
 *
 * 🚨 판정은 서버가 한다(작업지시서 7절). 여기서 보내는 것은 「무엇을 넣었나」 뿐이고, 결과 · 등급 · 점수 · 다음 턴 아이템은
 *    서버가 내려준다. 조합표는 받지 않는다 — 만든 아이템 한 개의 이름 · 그림만 온다.
 * 응답 봉투는 hc 규격이다 — 성공 { success, data_type, data } · 실패 { success: false, error_code, message }.
 */

export interface Item {
    key: string;
    name: string;
    emoji: string;
    group: string;
    depth: number;
    /** 0 일반 · 1 고급 · 2 희귀 · 3 영웅 · 4 전설 */
    grade: number;
    gradeLabel: string;
    /** 우리 작품 숨은 아이템이면 그 이야기로 가는 길 */
    story: { title: string; url: string } | null;
    /** 이번 턴 칸의 레시피 쪽지 — 솥에 못 넣고 펼쳐 읽는다 */
    note?: boolean;
}

/** 펼친 레시피 쪽지 — 결과와 재료 하나. 나머지 재료는 개수만(작업지시서 5절) */
export interface Note {
    result: { key: string; name: string; emoji: string; group: string; depth: number };
    shown: { key: string; name: string; emoji: string };
    hidden: number;
}

export interface Game {
    id: string;
    turn: number;
    turns: number;
    over: boolean;
    score: number;
    flame: number;
    flameMultiplier: number;
    /** 펼친 쪽지 칸은 null */
    offer: (Item | null)[];
    inventory: Item[];
    notes: Note[];
    /** 이번 주 보물 수수께끼 한 줄 */
    riddle: string | null;
    pending: Item | null;
    best: Item | null;
    firsts: number;
    madeCount: number;
    slots: { inventory: number; pot: number };
    week: { key: string; endsAt: string };
    itemTotal: number;
}

export interface Discovery {
    first: boolean;
    legend: boolean;
    claim: string | null;
}

export type TurnResult =
    | { outcome: 'pass'; points: 0 }
    | { outcome: 'boom'; reason: 'unknown' | 'max_grade'; points: 0 }
    | { outcome: 'made' | 'graded'; item: Item; points: number; bumped: boolean; bumpSteps: number; firstInGame: boolean; discovery: Discovery };

export interface BoardEntry {
    key: string;
    name: string;
    emoji: string;
    group: string;
    depth: number;
    kind: 'first' | 'legend';
    tag: string | null;
    at: string | null;
    makers: number;
}

export interface Board {
    week: { key: string; endsAt: string };
    itemTotal: number;
    /** 이번 주 보물 수수께끼 — 아무도 못 풀었으면 solved 가 null(보물 이름도 감춘다) */
    riddle: { text: string; solved: { key: string; name: string; emoji: string; tag: string | null } | null } | null;
    entries: BoardEntry[];
}

export interface TurnMove {
    game: string;
    settle?: 'keep' | 'discard' | null;
    replace?: number | null;
    pass?: boolean;
    offer?: number[];
    inventory?: number[];
}

export class PopApiError extends Error {
    constructor(
        public readonly code: string,
        message: string,
        public readonly status: number,
    ) {
        super(message);
    }
}

function csrf(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
        response = await fetch(path, {
            method,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                ...(body !== undefined ? { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() } : {}),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch {
        throw new PopApiError('POP_NETWORK', '연결이 끊겼어요. 잠시 뒤에 다시 해 주세요', 0);
    }

    const json = await response.json().catch(() => null);
    if (!response.ok || !json?.success) {
        throw new PopApiError(json?.error_code ?? 'POP_UNKNOWN', json?.message ?? '잠시 문제가 생겼어요', response.status);
    }

    return json.data as T;
}

export const api = {
    start: () => call<{ game: Game }>('POST', '/pop/alchemist/games', {}),
    resume: (id: string) => call<{ game: Game }>('GET', `/pop/alchemist/games/${encodeURIComponent(id)}`),
    note: (game: string, index: number) => call<{ note: Note; game: Game }>('POST', '/pop/alchemist/note', { game, index }),
    turn: (move: TurnMove) => call<{ result: TurnResult; game: Game }>('POST', '/pop/alchemist/turn', move),
    claim: (claim: string, tag: string) => call<{ tag: string }>('POST', '/pop/alchemist/claim', { claim, tag }),
    skip: (claim: string, game: string | null) => call<{ released: number }>('POST', '/pop/alchemist/claim/skip', { claim, game }),
    board: () => call<Board>('GET', '/pop/alchemist/board'),
};
